import NewLayout from "@components/new-ui/Layout";
import React from "react";
import { CasestudyCard, casestudyCards } from ".";
import { CaseStudyHerosection, CaseStudyInformation, FeatureList, Navbar, navLinks, OrganizationProfile } from "./alrannan-administrative-services";
import StatsInfo from "@components/new-ui/IndustriesSection/StatsInfo";

const Tarabily: React.FC = () => {
    return (
        <NewLayout
            title="Tarabily Co | WhatsApp Automation Success Story"
            description="Discover how Tarabily Co. achieved 100% business growth and customer satisfaction using Gallabox's WhatsApp automation solutions."
            canonical="https://gallabox.com/case-study/tarabily"
            ogAttributes={{ image: "https://assets.gallabox.com/gb-home/case-study/tarabily/tarabily-og.png" }}
        >
            <CaseStudyHerosection
                title="How Tarabily scaled to 100% growth and customer satisfaction with Gallabox"
                imageUrl={"https://assets.gallabox.com/gb-home/case-study/tarabily/tarabily-hero.png"}
                redirectUrl={"/case-study"}
            />
            <StatsInfo
                statsInfo={[
                    { percentage: "50%", label: " increase in lead generation" },
                    { percentage: "50%", label: "expansion in customer base" },
                    { percentage: "50%", label: "higher conversion rates for new customers" },
                ]}
            />

            <section className="max-w-7xl mx-auto  md:grid md:grid-cols-6 mt-24 mb-24">
                <div className="bg-white col-span-1">
                    <div style={{ position: "sticky", top: "100px" }}>
                        <OrganizationProfile
                            companyProfileData={[{ label: "Category", value: ["Printing & Design"] }]}
                            product={{
                                title: "Features Used",

                                productLink: [
                                    { link: "https://gallabox.com/whatsapp-chatbot", label: "Chatbots" },
                                    { link: "https://gallabox.com/whatsapp-flows", label: "Flows" },
                                    { link: "https://gallabox.com/click-to-whatsapp-ads", label: "Click-to-WhatsApp Ads" },
                                ],
                            }}
                        />
                    </div>
                </div>
                <div className=" col-span-4 md:pl-8 px-4">
                    <div className="flex flex-col gap-y-12 ">
                        <section id="About">
                            <p>
                                <a href="https://www.instagram.com/explore/locations/1034527604/tarabily-digital-press/?ref=Xr-ovPMfrHqDc" className="text-blue-500 pr-1">
                                    Tarabily Co.
                                </a>
                                is a digital print company based in Port Said, Egypt, serving businesses and individuals with custom printing solutions (such as banners, signage,
                                and promotional materials). In a competitive market, Tarabily has built a reputation on quality and responsiveness. As the company grew, its
                                leadership recognized the need for a more efficient, centralized approach to customer communication.
                            </p>
                        </section>

                        <section id="Challenges">
                            <CaseStudyInformation subTitle={"The challenge: Fragmented workflows across platforms"} className="mt-0" />

                            <p className="mt-2">
                                Before Gallabox, Tarabily&apos;s team managed customer conversations through individual WhatsApp accounts, leading to scattered interactions across
                                personal devices and desktops. Without centralized control, tracking leads, automating support, and ensuring consistent service was difficult.
                                Customer inquiries often went unanswered after hours, and leads from online ads were frequently lost due to delayed or missed responses. To support
                                their growth goals, the team recognized the need for a more streamlined and scalable communication workflow.
                            </p>

                            <FeatureList
                                List={[
                                    { listItem: "No centralized control made lead tracking and support automation impossible." },
                                    {
                                        listItem: "Inconsistent service quality due to fragmented workflows.",
                                    },
                                    { listItem: "Missed opportunities, like unanswered after-hours inquiries and lost leads from ads." },
                                ]}
                                className="mt-4 bg-gray-100"
                            />
                        </section>

                        <section id="Solutions">
                            <CaseStudyInformation subTitle={"The solution: Centralized automation on WhatsApp"} className="mt-0" />
                            <p className="mt-2">
                                Tarabily transformed its customer communication by implementing Gallabox&apos;s WhatsApp Business automation platform. The company deployed chatbots
                                to instantly address common inquiries about printing services, ensuring 24/7 responsiveness while qualifying leads for human agents. Structured
                                message flows guide prospects through interactive forms, capturing key details upfront to streamline follow-ups and quoting. A shared inbox
                                centralizes all WhatsApp conversations, enabling seamless team collaboration and preventing dropped chats.
                            </p>

                            <p className="mt-5">
                                To boost lead generation, Tarabily integrates Click-to-WhatsApp ads with Gallabox, directing social media inquiries into automated workflows.
                                Back-end integrations sync captured data with their CRM and order-management systems, eliminating manual entry and ensuring end-to-end tracking. By
                                automating routine tasks and unifying communication, Gallabox empowered Tarabily to scale operations while maintaining consistent, efficient
                                customer engagement.
                            </p>

                            <CaseStudyInformation subTitle={"So, how does Tarabily actually use Gallabox?"} className="mt-10" />
                            <p className="mt-2">Tarabily focused on four main objectives to revamp their communication workflow by utilizing Gallabox&apos;s suite of tools.</p>

                            <FeatureList
                                List={[
                                    { listItem: "Always-on customer service" },
                                    {
                                        listItem: "Frictionless order process",
                                    },
                                    { listItem: "Team collaboration made simple" },
                                    { listItem: "Social ads that actually convert" },
                                ]}
                                className="mt-4 bg-gray-100"
                            />
                            <p className="mt-5">
                                Chatbots: Automated chatbots now greet customers on WhatsApp and answer common questions 24/7. These bots provide instant information about printing
                                services (such as pricing, design options, and delivery times) and qualify inquiries before passing them to a human agent. This ensures no customer
                                ever waits on hold or misses a response.
                            </p>
                            <p className="mt-5">
                                Flows: The team built structured message flows to guide prospects through Tarabily&apos;s offerings. For example, a new inquiry is automatically
                                asked to provide details (like print job type and required quantity) through an interactive form. This systematic approach collects all key
                                information upfront, making follow-up calls and quotes faster and more accurate.
                            </p>

                            <p className="mt-5">
                                Click-to-WhatsApp Ads: Tarabily launched targeted Facebook and Instagram ads that open WhatsApp chats with the company when clicked. Each ad click
                                starts a conversation that goes directly into Gallabox&apos;s system. Leads generated from social media campaigns are automatically funneled into
                                the same automated workflows and team inbox, boosting the pipeline of new inquiries.
                            </p>
                        </section>

                        <section id="Impact">
                            <CaseStudyInformation subTitle={"Growth without growing pains"} className="mt-0" />
                            <p className="mt-2">
                                Tarabily&apos;s digital overhaul with Gallabox led to dramatic business results. Within months of implementation, key performance indicators showed
                                substantial improvement:
                            </p>

                            <p className="mt-5">
                                The results surpassed all projections. Within months of implementing Gallabox, Tarabily witnessed transformative growth across every key metric.
                            </p>

                            <p className="mt-5">
                                Mohammed Elkordy confirms Gallabox drove an astounding 100% impact on overall business growth – but the real success lies in the complete
                                operational transformation:
                            </p>

                            <FeatureList
                                List={[
                                    { listItem: "Customer satisfaction skyrocketed 100% with instant responses and 24/7 availability eliminating wait times" },
                                    { listItem: "New customer conversions doubled (100% growth) as automated follow-ups and personalized messaging perfected the sales process" },
                                    { listItem: "Active customer base expanded by 50% through powerful word-of-mouth from delighted clients" },
                                ]}
                                className="mt-5 bg-gray-100"
                            />

                            <p className="text-xl font-medium mt-6">The ripple effects were even more impressive:</p>

                            <FeatureList
                                List={[
                                    { listItem: "Employee productivity surged 100% as automation handled routine tasks" },
                                    { listItem: "Social media leads grew 50% with seamless Click-to-WhatsApp conversions" },
                                    { listItem: "Gross profits jumped 75% through streamlined operations" },
                                ]}
                                className="mt-5 bg-gray-100"
                            />

                            <p className="mt-5">
                                This wasn&apos;t just growth – it was a complete business revolution. By turning WhatsApp into a revenue-driving machine, Tarabily achieved more
                                with their existing team while delivering superior customer experiences that kept clients coming back.
                            </p>

                            <CaseStudyInformation subTitle={"Looking forward"} className="mt-10" />
                            <p className="mt-2">
                                Tarabily plans deeper automation of client onboarding and retention flows. Elkordy confirms a 100% likelihood to recommend Gallabox as they expand
                                their digital print services.
                            </p>
                        </section>
                    </div>
                </div>
                <div>
                    <div style={{ position: "sticky", top: "100px", minHeight: "510px" }} className="hidden md:block">
                        <Navbar links={navLinks} />
                    </div>
                </div>
            </section>
            <div className="max-w-6xl mx-auto">
                <section>
                    <h3 className="text-gray-900 mb-10 mt-5 text-left ml-4">Check out other customer stories</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 max-w-6xl mx-auto gap-10 mb-20">
                        {casestudyCards.slice(0, 3)?.map((data, index) => (
                            <CasestudyCard key={index} {...data} />
                        ))}
                    </div>
                </section>
            </div>
        </NewLayout>
    );
};

export default Tarabily;
